import React, { FC, useContext, useEffect, useState } from 'react'

import { Grid, Stack, useMediaQuery, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import { motion, PanInfo } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { CustomButton } from '../../components/CustomButton'
import { CustomLink } from '../../components/CustomLink'
import { EmptyAccountData } from '../../components/EmptyAccountData'
import { RefreshStats } from '../../components/Header/components'
import { CustomTabs } from '../../components/Tabs/CustomTabs'
import { TabType } from '../../components/Tabs/types'
import { DASHBOARD_LAYOUT_SWAPPED } from '../../constants'
import { RoleGuardedComponent } from '../../routes/guards'
import { APP_PATHS } from '../../routes/paths'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { LoadingStatusEnum } from '../../store/types'
import { runAutoUploadProcessRefreshData, UserContext, UserRolesEnum } from '../../user'
import { fetchCashFlowBalance } from '../CashFlow/CashFlowSlice'
import { ImportTypeEnum } from '../Settings/types'

import { DashboardCalendar } from './Calendar'
import { fetchDashboardWidgetsData, toggleCustomize } from './DashboardSlice'
import { ExclusiveBonusBanner } from './ExclusiveBonusBanner'
import { CommissionCompareWidget } from './widgets'
import { Positions } from './widgets/Positions'
import { WidgetsSection } from './WidgetsSection'

const DashboardPage: FC = () => {
  const { t } = useTranslation()
  const { spacing } = useTheme()

  const navigate = useNavigate()

  const dispatch = useAppDispatch()
  const { customize } = useAppSelector(({ dashboard }) => dashboard)
  const {
    userAccounts,
    accountSyncDateLoading,
    currentAccount,
    period,
    isRunningAutoUpload,
    lastAccountUpdate,
  } = useAppSelector(({ userAccounts }) => userAccounts)
  const { currentUser } = useContext(UserContext)

  const isAutoSync = currentAccount?.importType === ImportTypeEnum.autoSync

  const storedInitLayoutState = localStorage.getItem(DASHBOARD_LAYOUT_SWAPPED)
  const initLayoutState = storedInitLayoutState && JSON.parse(storedInitLayoutState)
  const [isSwapped, setIsSwapped] = useState<boolean>(initLayoutState)
  const [activeTab, setActiveTab] = useState<number>(0)

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const dashboardTabs: TabType<string>[] = [
    {
      key: 'widgets',
      label: t('widgets', 'Widgets'),
      component: <WidgetsSection />,
      className: 'tab-widgets',
    },
    {
      key: 'calendar',
      label: t('calendar', 'Calendar'),
      component: <DashboardCalendar />,
      className: 'tab-calendar',
    },
    {
      key: 'positions',
      label: t('positions', 'Positions'),
      component: <Positions />,
      className: 'tab-positions',
    },
  ]

  const handleDragEnd = (_: MouseEvent, info: PanInfo) => {
    const dragOffset = info.offset.x
    if (Math.abs(dragOffset) > 100) {
      setIsSwapped((prev) => {
        localStorage.setItem(DASHBOARD_LAYOUT_SWAPPED, JSON.stringify(!prev))
        return !prev
      })
    }
  }

  const handleCustomize = () => {
    dispatch(toggleCustomize())
  }

  const handleRefreshData = async () => {
    if (currentAccount) {
      dispatch(runAutoUploadProcessRefreshData(currentAccount.id))
    }
  }

  const handleImport = () => {
    navigate(APP_PATHS.importTrades)
  }

  useEffect(() => {
    if (userAccounts?.length > 0 && currentAccount?.id) {
      dispatch(fetchDashboardWidgetsData({ account: currentAccount.id, period }))
      dispatch(fetchCashFlowBalance({ account: currentAccount.id }))
    }
  }, [dispatch, userAccounts.length, currentAccount?.id, period])

  return (
    <Box height="100%" display="flex" flex={1} flexDirection="column" overflow="auto">
      <Grid
        container
        spacing={2}
        // gap={spacing(3)}
        alignItems="center"
        padding={spacing(1.5, 2)}
        // bgcolor={palette.primary.light}
      >
        {currentUser && !isMobile && (
          <Grid item md={6} lg={7}>
            <ExclusiveBonusBanner />
          </Grid>
        )}

        <Grid item xs={12} md={6} lg={5}>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
            <RefreshStats
              // isMobile={isMobile}
              onClick={handleRefreshData}
              isRunningAutoUpload={isRunningAutoUpload}
              lastAccountUpdate={lastAccountUpdate}
              isLoading={accountSyncDateLoading !== LoadingStatusEnum.Succeeded}
              isAutoSync={isAutoSync}
            />

            {!!userAccounts.length &&
              (customize ? (
                <CustomButton
                  sx={{
                    [theme.breakpoints.down('md')]: {
                      fontSize: 12,
                    },
                    borderRadius: spacing(0.5),
                  }}
                  onClick={handleCustomize}
                >
                  {t('done', 'Done')}
                </CustomButton>
              ) : (
                !isMobile && <CustomLink onClick={handleCustomize} />
              ))}

            <CustomButton
              className="manual-import-button"
              onClick={handleImport}
              sx={{
                [theme.breakpoints.down('md')]: {
                  fontSize: 12,
                },
                borderRadius: spacing(0.5),
              }}
            >
              {t('import')}
            </CustomButton>
          </Stack>
        </Grid>
      </Grid>

      <Box>
        {!userAccounts.length ? (
          <Box height="100%" display="flex">
            <EmptyAccountData />
          </Box>
        ) : (
          <Box m={spacing(2)}>
            {currentUser && isMobile && <ExclusiveBonusBanner />}

            {isMobile ? (
              <>
                <Box mt={2}>
                  <CustomTabs
                    tabs={dashboardTabs}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                  />
                </Box>
                <Box mt={spacing(2)}>{dashboardTabs[activeTab]?.component}</Box>
              </>
            ) : (
              <Box display="flex" pt={spacing(2)} gap={spacing(2)} flex={1} height="100%">
                <motion.div
                  drag={false}
                  onDragEnd={handleDragEnd}
                  style={{ order: isSwapped ? 2 : 1, flex: 1 }}
                  animate={{ x: 0 }}
                  dragConstraints={{ left: 0, right: 0 }}
                  dragTransition={{ bounceStiffness: 200, bounceDamping: 20 }}
                >
                  <WidgetsSection />
                </motion.div>
                <motion.div
                  key={isSwapped ? 'not-swapped' : 'swapped'}
                  drag={customize ? 'x' : false}
                  onDragEnd={handleDragEnd}
                  style={{
                    overflow: 'hidden',
                    order: isSwapped ? 1 : 2,
                    width: spacing(80.5),
                    cursor: customize ? 'pointer' : 'unset',
                  }}
                  animate={{ x: 0 }}
                  dragConstraints={{ left: 0, right: 0 }}
                  dragTransition={{ bounceStiffness: 200, bounceDamping: 20 }}
                >
                  <DashboardCalendar />
                  <Positions />
                  <RoleGuardedComponent roles={[UserRolesEnum.ADMIN]}>
                    <Box my={2} />
                    <CommissionCompareWidget />
                  </RoleGuardedComponent>
                </motion.div>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default DashboardPage
